import React from 'react';

import { useStyles } from '~/assets/styles';
import { CHAT_V2_LAYOUT } from '~/components/AiChat/constants/chatStyling';
import SafeComponent, { CriticalityLevel } from '~/components/core/SafeComponent';
import cn from '~/Utils/cn';

import { MESSAGE_VARIANT, type MessageLayoutProps, type MessageVariant } from './MessageTypes';

/**
 * Returns the background class for the message container based on variant.
 */
const getVariantBackground = (variant: MessageVariant) => {
  if (variant === MESSAGE_VARIANT.HUMAN) {
    return 'bg-azure-500/10';
  }
  return '';
};

/**
 * Returns computed CSS classes for message layout based on variant.
 */
const getLayoutClasses = (variant: MessageVariant) => {
  const isRightAligned = variant === MESSAGE_VARIANT.HUMAN;
  const variantBackground = getVariantBackground(variant);

  return {
    outer: cn('flex w-full flex-col self-center group', CHAT_V2_LAYOUT.maxWidthClass, {
      'items-end': isRightAligned,
    }),
    wrapper: cn({ [CHAT_V2_LAYOUT.humanMessage.width]: isRightAligned }),
    container: cn(
      'flex flex-col rounded-xl',
      variantBackground,
      isRightAligned ? CHAT_V2_LAYOUT.humanMessage.padding : 'w-full'
    ),
    innerFlex: cn('flex w-full rounded-xl p-8', isRightAligned ? 'flex-row-reverse pb-16' : 'flex-row'),
    isRightAligned,
  };
};

/**
 * Shared wrapper component for chat messages.
 * Handles outer container, avatar positioning, snackbar notifications, and SafeComponent wrapper.
 */
export const ChatSnackbarContext = React.createContext<(text: string) => void>(() => {});

export const MessageLayout: React.FC<MessageLayoutProps> = ({ avatar, variant, children, footer }) => {
  const { classes: styles } = useStyles() as unknown as { classes: { avatar: string } };
  const showSnackbar = React.useContext(ChatSnackbarContext);

  const modifiedAvatar = React.cloneElement(avatar, {
    className: cn('h-40 w-40', styles.avatar, avatar.props.className),
  });

  const layoutClasses = getLayoutClasses(variant);

  return (
    <SafeComponent
      location="AiChat/Message/MessageLayout"
      criticalityLevel={CriticalityLevel.HIGH}
      errorMetadata={{ component: 'MessageLayout', variant }}
    >
      <MessageLayoutContext.Provider
        value={{
          showSnackbar,
          layoutClasses,
        }}
      >
        <div className={layoutClasses.outer}>
          <div className={layoutClasses.wrapper}>
            <div className={layoutClasses.container}>
              <div className={layoutClasses.innerFlex}>
                <div className="mr-8 block">{modifiedAvatar}</div>
                <div className="flex flex-grow flex-col gap-4">{children}</div>
              </div>
            </div>
            {footer}
          </div>
        </div>
      </MessageLayoutContext.Provider>
    </SafeComponent>
  );
};

/**
 * Context to share layout utilities with child content components.
 */
interface MessageLayoutContextValue {
  showSnackbar: (text: string) => void;
  layoutClasses: ReturnType<typeof getLayoutClasses>;
}

export const MessageLayoutContext = React.createContext<MessageLayoutContextValue | null>(null);

/**
 * Hook to access MessageLayout context from child components.
 */
export const useMessageLayout = () => {
  const context = React.useContext(MessageLayoutContext);
  if (!context) {
    throw new Error('useMessageLayout must be used within a MessageLayout');
  }
  return context;
};
